import { useEffect, useRef, useState } from 'react';
import { shrinkPhoto } from '../assist/client';
import { labelPhotoPath, type LabelSide } from '../domain/label-photos';
import type { Wine } from '../domain/cellar';
import { keepPhoto } from '../photos/keep-photo';
import type { PhotoStorage } from '../store/fake-storage';
import { t, tf } from '../i18n';
import './LabelPhotosSection.css';

const SIDES: LabelSide[] = ['front', 'back'];

/**
 * "Étiquette" on a wine's page (owner request, 2026-10-11): the front and the back of the label,
 * as scanned or taken here. Tapping a missing side's empty area takes it with the camera (owner,
 * 2026-10-15: no separate button); a photo has a "×" in its corner to remove it after a confirmation (owner, 2026-10-15: no "Remplacer", a photo
 * is removed, then taken again). A photo is stored next to the cellar, then named on the wine with
 * `onTaken`; a removed one is unnamed with `onRemoved`, then deleted from the storage (owner,
 * 2026-10-15). If that delete fails, the photo is still gone from the wine.
 */
export function LabelPhotosSection({
  cellarId,
  wine,
  photos,
  onTaken,
  onRemoved,
}: {
  cellarId: string;
  wine: Wine;
  photos: PhotoStorage;
  onTaken: (side: LabelSide, photoId: string) => Promise<unknown>;
  onRemoved: (side: LabelSide) => Promise<unknown>;
}) {
  const [error, setError] = useState<string | undefined>();
  return (
    <section className="wine-detail__section label-photos" aria-labelledby="wine-label">
      <h2 id="wine-label">{t('photos.title')}</h2>
      {error && <p role="alert">{error}</p>}
      <div className="label-photos__sides">
        {SIDES.map((side) => (
          <LabelPhotoSide
            key={side}
            side={side}
            cellarId={cellarId}
            wineName={`${wine.domaine} ${wine.annee}`}
            photoId={wine.labelPhotos?.[side]}
            photos={photos}
            onTake={async (file) => {
              setError(undefined);
              try {
                const id = await keepPhoto(photos, cellarId, await shrinkPhoto(file));
                await onTaken(side, id);
              } catch {
                setError(t('photos.failed'));
              }
            }}
            onRemove={() => {
              setError(undefined);
              const photoId = wine.labelPhotos?.[side];
              onRemoved(side)
                .then(() =>
                  photoId
                    ? photos.delete(labelPhotoPath(cellarId, photoId)).catch(() => undefined)
                    : undefined,
                )
                .catch(() => setError(t('error.saveFailed')));
            }}
          />
        ))}
      </div>
    </section>
  );
}

function LabelPhotoSide({
  side,
  cellarId,
  wineName,
  photoId,
  photos,
  onTake,
  onRemove,
}: {
  side: LabelSide;
  cellarId: string;
  wineName: string;
  photoId: string | undefined;
  photos: PhotoStorage;
  onTake: (file: File) => Promise<void>;
  onRemove: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const label = t(side === 'front' ? 'photos.front' : 'photos.back');

  // Read from storage only when there is a photo; the browser URL is freed when it changes.
  useEffect(() => {
    if (!photoId) {
      setUrl(undefined);
      return;
    }
    let made: string | undefined;
    let current = true;
    photos
      .get(labelPhotoPath(cellarId, photoId))
      .then((bytes) => {
        if (!current || !bytes) return;
        made = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: 'image/jpeg' }));
        setUrl(made);
      })
      .catch(() => setUrl(undefined));
    return () => {
      current = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [photos, cellarId, photoId]);

  return (
    <figure className="label-photos__side">
      <div className="label-photos__frame">
        {url ? (
          <a href={url} target="_blank" rel="noreferrer" className="label-photos__link">
            <img src={url} alt={tf('photos.alt', { wine: wineName, side: label })} />
          </a>
        ) : photoId ? (
          <div className="label-photos__empty">{t('photos.loading')}</div>
        ) : (
          // The empty area itself takes the photo (owner, 2026-10-15: no separate button).
          <button
            type="button"
            className="label-photos__empty label-photos__take"
            disabled={busy}
            aria-label={t(side === 'front' ? 'photos.takeFront' : 'photos.takeBack')}
            onClick={() => input.current?.click()}
          >
            {busy ? t('photos.saving') : t('photos.none')}
          </button>
        )}
        {photoId && !confirming && (
          <button
            type="button"
            className="label-photos__remove"
            aria-label={t(side === 'front' ? 'photos.removeFront' : 'photos.removeBack')}
            onClick={() => setConfirming(true)}
          >
            ×
          </button>
        )}
        {photoId && confirming && (
          <div className="label-photos__confirm" role="group" aria-label={label}>
            <p>{t('photos.removeConfirm')}</p>
            <button type="button" onClick={() => setConfirming(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="label-photos__confirm-delete"
              onClick={() => {
                setConfirming(false);
                onRemove();
              }}
            >
              {t('common.delete')}
            </button>
          </div>
        )}
      </div>
      <figcaption>{label}</figcaption>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        aria-label={label}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          setBusy(true);
          void onTake(file).finally(() => setBusy(false));
        }}
      />
    </figure>
  );
}
